/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from "vuex";
import individualDiagnose from "../../../../share/util/individualDiagnose";
import { apiErrorDefaultAction } from '../../../../share/util/api/errorAction';
import { PHASE, TIME } from "./constants";
import { readData } from "./readData";
import { execute, executeHandler } from "./execute";
import { pollingStop } from "./pollingStop";
import { monitoring } from "./monitoring";
import { exit } from "./exit";

export default {
  name: "requirement008",
  data() {
    return {
      phase: PHASE.STANDBY_ENGINE_START,
      isLoading: false,
      coolingTime: null,
      timer: 0,
      timerInfo: "",
      modal_requirement_008: false,
      executeResponse: null,
      isExecuteUnexpectedError: false,
      isCancelled: false,
      isExecuteProcess: false,
      isCancelProcess: false,
      gpfErrorTxt: ''
    };
  },

  props: {
    i18nWsKey: { type: String, default: "", required: true },
    workSupportId: { type: Number, default: "", required: true },
  },

  computed: {
    ...mapState({
      netAppId: (state) => state.netAppConnector.pairingAppId,
    }),

    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.START_CONDITION_ERROR:
        case PHASE.GPF_ERROR:
        case PHASE.COMMUNICATION_ERROR:
        case PHASE.COMMON_ERROR:
          return "danger";
        case PHASE.SUCCESS:
        case PHASE.COOLING_COMPLETED:
          return "success";
        case PHASE.STANDBY_FORCED_PLAYBACK:
        case PHASE.STANDBY_PLAYBACK:
        case PHASE.EXECUTION:
        case PHASE.EXECUTION_MONITORING_STATUS:
        case PHASE.EXECUTION_MONITORING_CANCEL:
        case PHASE.COOLING_DISPLAY:
        case PHASE.IGOFF_INSTRUCTION_DISPLAY:
        case PHASE.IGOFF_STANDBY:
        default:
          return "secondary";
      }
    },

    /**
     * ダイアログのヘッダー文言
     */
    headerTitle() {
      // 確認
      const confirmationTitle = 'message.common_title_confirmation';
      // 実行中
      const executionTitle = 'individual.common_title_execution';
      // 成功
      const successTitle = 'message.common_title_success';
      // エラー
      const errorTitle = 'message.common_title_error';

      const titleMap = {
        [PHASE.STANDBY_ENGINE_START]: confirmationTitle,
        [PHASE.STANDBY_FORCED_PLAYBACK]: confirmationTitle,
        [PHASE.STANDBY_PLAYBACK]: confirmationTitle,
        [PHASE.EXECUTION]: confirmationTitle,
        [PHASE.EXECUTION_MONITORING_STATUS]: executionTitle,
        [PHASE.EXECUTION_MONITORING_CANCEL]: confirmationTitle,
        [PHASE.COOLING_DISPLAY]: executionTitle,
        [PHASE.IGOFF_INSTRUCTION_DISPLAY]: confirmationTitle,
        [PHASE.IGOFF_STANDBY]: executionTitle,
        [PHASE.SUCCESS]: successTitle,
        [PHASE.COOLING_COMPLETED]: successTitle,
        [PHASE.START_CONDITION_ERROR]: errorTitle,
        [PHASE.GPF_ERROR]: errorTitle,
        [PHASE.COMMUNICATION_ERROR]: errorTitle,
        [PHASE.COMMON_ERROR]: errorTitle
      };
      return this.$t(titleMap[this.phase]);
    },

    /**
     * ダイアログの表示文言
     */
    workSupportMessage() {
      const messageMap = {
        [PHASE.STANDBY_ENGINE_START]: `${this.i18nWsKey}.REQ_WS_8_TXT_1`,
        [PHASE.STANDBY_FORCED_PLAYBACK]: `${this.i18nWsKey}.REQ_WS_8_TXT_2`,
        [PHASE.STANDBY_PLAYBACK]: `${this.i18nWsKey}.REQ_WS_8_TXT_3`,
        [PHASE.EXECUTION]: `${this.i18nWsKey}.REQ_WS_8_TXT_4`,
        [PHASE.EXECUTION_MONITORING_STATUS]: `${this.i18nWsKey}.REQ_WS_8_TXT_5_0000`,
        [PHASE.EXECUTION_MONITORING_CANCEL]: `${this.i18nWsKey}.REQ_WS_8_TXT_7`,
        [PHASE.COOLING_DISPLAY]: this.timerInfo,
        [PHASE.IGOFF_INSTRUCTION_DISPLAY]: `${this.i18nWsKey}.REQ_WS_8_TXT_9`,
        [PHASE.IGOFF_STANDBY]: this.timerInfo,
        [PHASE.SUCCESS]: `${this.i18nWsKey}.REQ_WS_8_TXT_11`,
        [PHASE.COOLING_COMPLETED]: `${this.i18nWsKey}.REQ_WS_8_TXT_13`,
        [PHASE.START_CONDITION_ERROR]: `${this.i18nWsKey}.REQ_WS_8_TXT_14`,
        [PHASE.GPF_ERROR]: this.gpfErrorTxt,
        [PHASE.COMMUNICATION_ERROR]: `${this.i18nWsKey}.REQ_WS_8_TXT_16`,
        [PHASE.COMMON_ERROR]: `individual.error_communication_error`,
      };
      return this.$te(messageMap[this.phase]) ? this.$t(messageMap[this.phase]) : messageMap[this.phase];
    },

    /**
     * ダイアログのキャンセルボタンの表示有無
     * @returns {boolean} 表示する場合はtrueを返す
     */
    showButtonCancel() {
      const showCancelButtonMap = {
        [PHASE.STANDBY_FORCED_PLAYBACK]: true,
        [PHASE.STANDBY_PLAYBACK]: true,
        [PHASE.EXECUTION]: true,
        [PHASE.EXECUTION_MONITORING_STATUS]: true,
        [PHASE.EXECUTION_MONITORING_CANCEL]: true
      };
      let result = showCancelButtonMap[this.phase];
      if (!result) {
        // 合致しない場合は false とする。
        result = false;
      }
      return result;
    },

    /**
     * ダイアログのOKボタンの表示有無
     * @returns {boolean} 表示する場合はtrueを返す
     */
    showButtonOk() {
      const showOkButtonMap = {
        [PHASE.STANDBY_ENGINE_START]: true,
        [PHASE.STANDBY_FORCED_PLAYBACK]: true,
        [PHASE.STANDBY_PLAYBACK]: true,
        [PHASE.EXECUTION]: true,
        [PHASE.EXECUTION_MONITORING_CANCEL]: true,
        [PHASE.IGOFF_INSTRUCTION_DISPLAY]: true,
        [PHASE.SUCCESS]: true,
        [PHASE.COOLING_COMPLETED]: true,
        [PHASE.START_CONDITION_ERROR]: true,
        [PHASE.GPF_ERROR]: true,
        [PHASE.COMMUNICATION_ERROR]: true,
        [PHASE.COMMON_ERROR]: true
      };
      let result = showOkButtonMap[this.phase];
      if (!result) {
        // 合致しない場合は false とする。
        result = false;
      }
      return result;
    },

    /**
     * キャンセルボタンのボタン活性化有無
     * @returns {boolean} 非活性の場合はtrueを返す
     */
    buttonCancelDisabled() {
      // GPF再生状態確認とGPF再生中止確認以外は isLoading の値をもとに活性/非活性を判定する
      let disabledFlag = this.isLoading;
      if (
        (this.isExecuteProcess && this.phase === PHASE.EXECUTION_MONITORING_STATUS) ||
        (this.phase === PHASE.EXECUTION_MONITORING_CANCEL && !this.isCancelProcess)
      ) { disabledFlag = false; }
      return disabledFlag;
    },

    /**
     * OKボタンのボタン活性化有無
     * @returns {boolean} 非活性の場合はtrueを返す
     */
    buttonOkDisabled() {
      // GPF再生中止確認以外は isLoading の値をもとに活性/非活性を判定する
      let disabledFlag = this.isLoading;
      if (this.phase === PHASE.EXECUTION_MONITORING_CANCEL && !this.isCancelProcess) {
        disabledFlag = false;
      }
      return disabledFlag;
    }
  },

  methods: {
    ...mapActions("auth", ["logout", "resetSession"]),

    /** 初期化処理 */
    async init() {
      this.systemId = individualDiagnose.getCurrentSystemId(this);
      this.modal_requirement_008 = true;
    },

    /** Wizardを閉じる */
    closeWizard() {
      this.$emit("close-wizard");
      this.modal_requirement_008 = false;
    },

    //----- PHASE操作メソッド -----------------------------------------------//

    /** 実行ダイアログを表示 */
    showExecution() {
      this.phase = PHASE.EXECUTION;
    },

    /** GPF再生中止確認表示 */
    handleCancelClick() {
      this.isCancelled = true;
      this.phase = PHASE.EXECUTION_MONITORING_CANCEL;
    },

    //----- バックエンドAPI実行メソッド -----------------------------------------------//

    /** データ読出し処理 */
    async readDataProcess() {
      // データ読出しAPI実行
      await readData(this);
    },

    /** 実行処理 */
    async executeProcess() {
      // 実行API実行
      await execute(this);
    },

    /** 実行処理結果ハンドラ（GPF再生中止確認表示キャンセル押下時に実行したいためexecuteから分離） */
    async executeHandler() {
      await executeHandler(this);
    },

    /** 中断処理 */
    async pollingStopProcess() {
      // ボタン押下可否フラグ変更
      this.isCancelProcess = true;
      // 中断API実行
      await pollingStop(this);
    },

    /**
     * 停止監視処理(通常監視/中断監視)
     * @param {string} phase 
     */
    async monitoringProcess(phase) {
      // 停止監視API実行
      await monitoring(this, phase);
    },

    /** 終了処理 */
    async exitProcess() {
      // 終了API実行
      await exit(this);
    },

    /** 
     * 冷却中表示
     * @param {string} phase 
     */
    async cooling(phase) {
      this.isLoading = true;

      let timerInfoText, nextPhase = '';
      if (phase === PHASE.EXECUTION_MONITORING_STATUS) {
        // 通常停止から遷移
        timerInfoText = this.$t(`${this.i18nWsKey}.REQ_WS_8_TXT_8_0002`);
        nextPhase = PHASE.IGOFF_INSTRUCTION_DISPLAY;
      } else {
        // 中断から遷移
        timerInfoText = this.$t(`${this.i18nWsKey}.REQ_WS_8_TXT_12`);
        nextPhase = PHASE.COOLING_COMPLETED;
      }

      // 初期表示
      let displayTime = TIME.INITIAL_TIME_FOR_COOLING;
      this.timerInfo = timerInfoText.replace("$TIME$", Math.ceil(displayTime / 60000));
      this.phase = PHASE.COOLING_DISPLAY;

      return new Promise((resolve) => {
        this.coolingTime = setInterval(() => {
          this.timer += TIME.INTERVAL_FOR_COOLING;
          displayTime -= TIME.INTERVAL_FOR_COOLING;
          // 指定のカウント時間が終了したらIGOFF 指示表示に遷移する
          if (this.timer >= TIME.INITIAL_TIME_FOR_COOLING || displayTime <= 0) {
            this.isLoading = false;
            clearInterval(this.coolingTime);
            this.timer = 0;
            this.timerInfo = '';
            this.phase = nextPhase;
            resolve();
          }

          this.timerInfo = timerInfoText.replace("$TIME$", Math.ceil(displayTime / 60000));
        }, TIME.INTERVAL_FOR_COOLING);
      });
    },

    /** IGOFF待機処理 */
    async igOffStandby() {
      this.isLoading = true;

      let displayTime = TIME.INITIAL_TIME_FOR_IGOFF;
      this.timerInfo = this.$t(`${this.i18nWsKey}.REQ_WS_8_TXT_10`).replace("$TIME$", Math.ceil(displayTime / 1000));
      this.phase = PHASE.IGOFF_STANDBY;

      this.coolingTime = setInterval(() => {
        this.timer += TIME.INTERVAL_FOR_IGOFF;
        displayTime -= TIME.INTERVAL_FOR_IGOFF;
        // 指定のカウント時間が終了したらIGOFF 指示表示に遷移する
        if (this.timer >= TIME.INITIAL_TIME_FOR_IGOFF || displayTime <= 0) {
          this.isLoading = false;
          clearInterval(this.coolingTime);
          this.timer = 0;
          this.timerInfo = '';
          this.phase = PHASE.SUCCESS;
        }

        this.timerInfo = this.$t(`${this.i18nWsKey}.REQ_WS_8_TXT_10`).replace("$TIME$", Math.ceil(displayTime / 1000));
      }, TIME.INTERVAL_FOR_IGOFF);
    },

    /**
     * キャンセルボタン押下後の処理
     */
    onCancelClicked() {
      switch (this.phase) {
        case PHASE.STANDBY_FORCED_PLAYBACK:
        case PHASE.STANDBY_PLAYBACK:
        case PHASE.EXECUTION:
          this.closeWizard();
          break;
        case PHASE.EXECUTION_MONITORING_STATUS:
          this.handleCancelClick();
          break;
        case PHASE.EXECUTION_MONITORING_CANCEL:
          this.isCancelled = false;
          this.phase = PHASE.EXECUTION_MONITORING_STATUS;
          // executeAPI実行完了している場合のみ、executeAPIレスポンスに対する処理を実行
          if (!this.isExecuteProcess) {
            this.$nextTick(() => { this.executeHandler(); });
          }
          break;
      }
    },

    /**
     * OKボタン押下後の処理
     */
    async onOkClicked() {
      switch (this.phase) {
        case PHASE.STANDBY_ENGINE_START:
          await this.readDataProcess();
          break;
        case PHASE.STANDBY_FORCED_PLAYBACK:
        case PHASE.STANDBY_PLAYBACK:
          this.showExecution();
          break;
        case PHASE.EXECUTION:
          await this.executeProcess();
          break;
        case PHASE.EXECUTION_MONITORING_CANCEL:
        case PHASE.GPF_ERROR:
          await this.pollingStopProcess();
          break;
        case PHASE.IGOFF_INSTRUCTION_DISPLAY:
          await this.igOffStandby();
          break;
        case PHASE.SUCCESS:
        case PHASE.COOLING_COMPLETED:
        case PHASE.START_CONDITION_ERROR:
          this.closeWizard();
          break;
        case PHASE.COMMON_ERROR:
          await this.exitProcess();
          break;
        case PHASE.COMMUNICATION_ERROR:
          await this.monitoringProcess(this.phase);
          break;
        default:
          break;
      }
    },

    //----- 異常コールバックメソッド -----------------------------------------------//

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /** 共通エラー */
    showUnexpectedError() {
      this.phase = PHASE.COMMON_ERROR;
    },
  },

  mounted() {
    this.init();
  },
};